import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import axios from 'axios'
import { z } from 'zod'
import { supabase } from '@/shared/lib/supabase/client'
import { toast } from '@/shared/lib/toast'

// API Client Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Extend InternalAxiosRequestConfig to include _retry flag
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

// Add auth token interceptor — uses proactive token validation
apiClient.interceptors.request.use(async config => {
  const accessToken = await getValidAccessToken()
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  // Add tenant and environment context
  const tenantId = localStorage.getItem('current_tenant_id')
  const environmentId = localStorage.getItem('current_environment_id')

  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId
  }
  if (environmentId) {
    config.headers['X-Environment-ID'] = environmentId
  }

  return config
})

// Shared refresh promise — deduplicates concurrent refresh attempts
let refreshPromise: Promise<string | null> | null = null

function getTokenExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.')
    const decoded = JSON.parse(atob(payload))
    return decoded.exp ?? null
  } catch {
    return null
  }
}

async function getValidAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) return null

  // Check if token is still valid (with 30s buffer)
  const exp = getTokenExpiry(session.access_token)
  const now = Math.floor(Date.now() / 1000)
  if (exp && exp > now + 30) {
    return session.access_token // Token is fresh
  }

  // Token expired or about to — refresh (deduped)
  if (!refreshPromise) {
    refreshPromise = supabase.auth
      .refreshSession()
      .then(({ data }) => {
        refreshPromise = null
        return data.session?.access_token ?? null
      })
      .catch(() => {
        refreshPromise = null
        return null
      })
  }

  return refreshPromise
}

// Thundering-herd guard for forceLogout
let isLoggingOut = false

// Reset logout guard (called after successful login to allow future logouts)
export function resetLogoutGuard() {
  isLoggingOut = false
}

// Check if a logout is already in progress (used by QueryCache to avoid racing)
export function isLoggingOutGuard() {
  return isLoggingOut
}

// Add response interceptor — thin 401 safety net (deduped via shared promise)
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    if (
      error.response?.status === 401 &&
      !isLoggingOut &&
      error.config &&
      !error.config._retry
    ) {
      error.config._retry = true

      // Use getValidAccessToken which deduplicates via shared promise
      const accessToken = await getValidAccessToken()
      if (accessToken) {
        error.config.headers.Authorization = `Bearer ${accessToken}`
        return apiClient(error.config)
      }

      // Refresh truly failed — force logout
      const { useAuthStore } = await import('@/features/auth/stores/authStore')
      isLoggingOut = true
      useAuthStore.getState().forceLogout()
    }
    return Promise.reject(error)
  }
)

/**
 * Type-safe API request with Zod validation
 */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T>
export async function apiRequest<TResponse extends z.ZodTypeAny>(
  config: AxiosRequestConfig,
  responseSchema: TResponse
): Promise<z.infer<TResponse>>
export async function apiRequest(
  config: AxiosRequestConfig,
  responseSchema?: z.ZodTypeAny
): Promise<unknown> {
  try {
    const response: AxiosResponse = await apiClient.request(config)

    if (responseSchema) {
      return responseSchema.parse(response.data)
    }

    return response.data
  } catch (error) {
    if (error instanceof z.ZodError) {
      toast.error('Invalid response from server')
      throw new Error('Invalid response from server')
    }
    throw error
  }
}

/**
 * Helper function to make GET requests
 */
export function get<T>(url: string): Promise<T>
export function get<TResponse extends z.ZodTypeAny>(
  url: string,
  responseSchema: TResponse,
  config?: AxiosRequestConfig
): Promise<z.infer<TResponse>>
export function get(
  url: string,
  responseSchema?: z.ZodTypeAny,
  config?: AxiosRequestConfig
): Promise<unknown> {
  return apiRequest(
    { method: 'GET', url, ...config },
    responseSchema as z.ZodTypeAny
  )
}

/**
 * Helper function to make POST requests
 */
export function post<T>(url: string, data?: unknown): Promise<T>
export function post<TResponse extends z.ZodTypeAny>(
  url: string,
  data: unknown,
  responseSchema: TResponse,
  config?: AxiosRequestConfig
): Promise<z.infer<TResponse>>
export function post(
  url: string,
  data?: unknown,
  responseSchema?: z.ZodTypeAny,
  config?: AxiosRequestConfig
): Promise<unknown> {
  return apiRequest(
    { method: 'POST', url, data, ...config },
    responseSchema as z.ZodTypeAny
  )
}

/**
 * Helper function to make PUT requests
 */
export function put<T>(url: string, data?: unknown): Promise<T>
export function put<TResponse extends z.ZodTypeAny>(
  url: string,
  data: unknown,
  responseSchema: TResponse,
  config?: AxiosRequestConfig
): Promise<z.infer<TResponse>>
export function put(
  url: string,
  data?: unknown,
  responseSchema?: z.ZodTypeAny,
  config?: AxiosRequestConfig
): Promise<unknown> {
  return apiRequest(
    { method: 'PUT', url, data, ...config },
    responseSchema as z.ZodTypeAny
  )
}

/**
 * Helper function to make PATCH requests
 */
export function patch<T>(url: string, data?: unknown): Promise<T>
export function patch<TResponse extends z.ZodTypeAny>(
  url: string,
  data: unknown,
  responseSchema: TResponse,
  config?: AxiosRequestConfig
): Promise<z.infer<TResponse>>
export function patch(
  url: string,
  data?: unknown,
  responseSchema?: z.ZodTypeAny,
  config?: AxiosRequestConfig
): Promise<unknown> {
  return apiRequest(
    { method: 'PATCH', url, data, ...config },
    responseSchema as z.ZodTypeAny
  )
}

/**
 * Helper function to make DELETE requests
 */
export function del<T>(url: string): Promise<T>
export function del<TResponse extends z.ZodTypeAny>(
  url: string,
  responseSchema: TResponse,
  config?: AxiosRequestConfig
): Promise<z.infer<TResponse>>
export function del(
  url: string,
  responseSchema?: z.ZodTypeAny,
  config?: AxiosRequestConfig
): Promise<unknown> {
  return apiRequest(
    { method: 'DELETE', url, ...config },
    responseSchema as z.ZodTypeAny
  )
}
